import React, { useContext, useEffect, useState, useRef } from 'react'
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';

import { APIProvider, Map, AdvancedMarker, useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import { Checkbox, FormControlLabel, FormGroup, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Switch, Autocomplete, TextField, CircularProgress, Button, Chip } from '@mui/material';
import WizardDataContext from '../../../store/WizardDataContext';
import { LocationOn, MyLocation, Search } from '@mui/icons-material';
import { InputAdornment } from '@mui/material';

const filterAddress = (address) => {
  if (!address || typeof address !== 'string') return address;

  let filtered = address.trim();

  filtered = filtered.replace(/[A-Z0-9]{2,6}\+[A-Z0-9]{2,6},\s*/g, '');
  filtered = filtered.replace(/^[A-Z0-9]{2,6}\+[A-Z0-9]{2,6}\s+/g, '');
  filtered = filtered.replace(/\s+[A-Z0-9]{2,6}\+[A-Z0-9]{2,6}\s+/g, ' ');
  filtered = filtered.replace(/\s+[A-Z0-9]{2,6}\+[A-Z0-9]{2,6}$/g, '');
  filtered = filtered.replace(/[A-Z0-9]{2,6}\+[A-Z0-9]{2,6}[,\s]*/g, '');
  filtered = filtered.replace(/,\s*,/g, ',');
  filtered = filtered.replace(/^\s*,\s*/, '');
  filtered = filtered.replace(/\s*,\s*$/, '');
  filtered = filtered.replace(/\s+/g, ' ');
  filtered = filtered.replace(/,\s*$/, '');

  return filtered.trim();
};

const pickBestGeocodeResult = (results) => {
  if (!results?.length) return null;
  return results.reduce((best, result) => {
    const candidate = result.formatted_address || '';
    const bestAddress = best.formatted_address || '';
    return candidate.split(',').length > bestAddress.split(',').length ? result : best;
  }, results[0]);
};

const LocationMapGeocoder = ({
  mapKey,
  mapLocation,
  zoom,
  searchQuery,
  setIsSearching,
  onSelectLocation,
  onAddressResolved,
  reverseGeocodeRef,
}) => {
  const map = useMap();
  const geocodingLib = useMapsLibrary('geocoding');
  const geocoderRef = useRef(null);
  const pendingReverseGeocode = useRef(null);

  // Hold callbacks in refs so the search effect doesn't re-run on every parent
  // render (parent passes inline arrow functions that change identity each render).
  const onSelectLocationRef = useRef(onSelectLocation);
  const onAddressResolvedRef = useRef(onAddressResolved);
  const setIsSearchingRef = useRef(setIsSearching);
  onSelectLocationRef.current = onSelectLocation;
  onAddressResolvedRef.current = onAddressResolved;
  setIsSearchingRef.current = setIsSearching;

  useEffect(() => {
    if (geocodingLib) {
      geocoderRef.current = new geocodingLib.Geocoder();
      if (pendingReverseGeocode.current) {
        const { lat, lng } = pendingReverseGeocode.current;
        pendingReverseGeocode.current = null;
        geocoderRef.current.geocode({ location: { lat, lng } }, (results, status) => {
          if (status !== 'OK') return;
          const bestResult = pickBestGeocodeResult(results);
          if (bestResult?.formatted_address) {
            onAddressResolvedRef.current(filterAddress(bestResult.formatted_address));
          }
        });
      }
    }
  }, [geocodingLib]);

  reverseGeocodeRef.current = (lat, lng) => {
    const geocoder = geocoderRef.current;
    if (!geocoder) {
      pendingReverseGeocode.current = { lat, lng };
      return;
    }

    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status !== 'OK') return;
      const bestResult = pickBestGeocodeResult(results);
      if (bestResult?.formatted_address) {
        onAddressResolvedRef.current(filterAddress(bestResult.formatted_address));
      }
    });
  };

  useEffect(() => {
    if (!geocodingLib) return;
    const geocoder = geocoderRef.current;
    if (!geocoder) return;

    const query = searchQuery.trim();
    if (query.length < 3) {
      setIsSearchingRef.current(false);
      return;
    }

    setIsSearchingRef.current(true);
    const timeoutId = setTimeout(() => {
      geocoder.geocode({ address: query }, (results, status) => {
        setIsSearchingRef.current(false);
        if (status !== 'OK') return;

        const bestResult = pickBestGeocodeResult(results);
        if (!bestResult) return;

        const location = bestResult.geometry.location;
        const newLocation = { lat: location.lat(), lng: location.lng() };
        onSelectLocationRef.current(newLocation, 15);
        onAddressResolvedRef.current(filterAddress(bestResult.formatted_address));
        map?.panTo(newLocation);
        map?.setZoom(15);
      });
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, geocodingLib, map]);

  useEffect(() => {
    if (!map || !mapLocation) return;
    map.panTo(mapLocation);
    if (zoom) map.setZoom(zoom);
  }, [mapKey, mapLocation, zoom, map]);

  return null;
};

const LocationMapPicker = ({
  mapKey,
  mapLocation,
  zoom,
  selectedLocation,
  searchQuery,
  onSearchChange,
  isSearching,
  setIsSearching,
  onSelectLocation,
  onAddressResolved,
  hasUserLocation,
  isDetectingLocation,
  onDetectLocation,
  onClose,
  reverseGeocodeRef,
}) => {
  const handleMapClick = (e) => {
    const latLng = e.detail.latLng;
    if (!latLng) return;

    const { lat, lng } = latLng;
    // Marker-only update: don't touch mapLocation/zoom so we don't pan or
    // reset zoom to a stale state value when the user has zoomed in via gestures.
    onSelectLocation({ lat, lng }, null, { markerOnly: true });
    reverseGeocodeRef.current?.(lat, lng);
  };

  return (
    <div className="new-location" style={{ position: 'relative' }}>
      <Map
        key={mapKey}
        defaultCenter={mapLocation}
        defaultZoom={zoom}
        gestureHandling="greedy"
        onClick={handleMapClick}
        mapTypeControl={false}
        fullscreenControl={false}
        style={{ width: '100%', height: '100%' }}
        mapId="step7-map"
      >
        {selectedLocation?.lat != null && selectedLocation?.lng != null && (
          <AdvancedMarker position={selectedLocation} />
        )}
        <LocationMapGeocoder
          mapKey={mapKey}
          mapLocation={mapLocation}
          zoom={zoom}
          searchQuery={searchQuery}
          setIsSearching={setIsSearching}
          onSelectLocation={onSelectLocation}
          onAddressResolved={onAddressResolved}
          reverseGeocodeRef={reverseGeocodeRef}
        />
      </Map>

      <div style={{
        position: 'absolute',
        top: '15px',
        left: '15px',
        width: '50%',
        maxWidth: '400px',
        zIndex: 1000,
      }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search for a location..."
          value={searchQuery}
          onChange={onSearchChange}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {isSearching ? (
                  <CircularProgress size={20} style={{ color: '#666' }} />
                ) : (
                  <Search style={{ color: '#666' }} />
                )}
              </InputAdornment>
            ),
            style: {
              backgroundColor: 'white',
              borderRadius: '8px',
              boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
            },
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': { borderColor: 'transparent' },
              '&.Mui-focused fieldset': { borderColor: '#0A71F5' },
            },
          }}
        />
      </div>

      <div style={{ position: 'absolute', top: '15px', right: '60px', zIndex: 1000 }}>
        <Button
          variant="contained"
          size="small"
          onClick={() => onDetectLocation(reverseGeocodeRef.current)}
          disabled={isDetectingLocation}
          style={{
            backgroundColor: hasUserLocation ? '#4caf50' : '#1976d2',
            color: 'white',
            minWidth: '40px',
            minHeight: '40px',
            padding: '8px',
            borderRadius: '50%',
            boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
            border: 'none',
          }}
        >
          {isDetectingLocation ? (
            <CircularProgress size={20} style={{ color: 'white' }} />
          ) : (
            <MyLocation style={{ fontSize: '20px' }} />
          )}
        </Button>
      </div>

      <div style={{ position: 'absolute', top: '15px', right: '15px', zIndex: 1000 }}>
        <Button
          variant="contained"
          size="small"
          onClick={onClose}
          style={{
            backgroundColor: 'white',
            color: '#666',
            minWidth: '40px',
            minHeight: '40px',
            padding: '8px',
            borderRadius: '50%',
            boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
            border: 'none',
          }}
        >
          <CloseRoundedIcon style={{ fontSize: '20px' }} />
        </Button>
      </div>
    </div>
  );
};

const Locations = ({ setDisableBtn }) => {

  const wizardCtx = useContext(WizardDataContext)
  const { wizardData, updateData } = wizardCtx;
  const locationsData = wizardData.locations;
  const isPopin = wizardData.dealSelection?.selectedDeal === 'popin';

  // South Africa default location (centered on Johannesburg area)
  const DefaultLocation = { lat: -26.2041, lng: 28.0473 };
  const DefaultZoom = 6;

  const [modalOpen, setModalOpen] = useState(false);
  const handleOpenModal = () => {
    // Reset all fields when opening modal
    setAddress('');
    setAddressTitle('');
    setSearchQuery('');
    setSelectedLocation(DefaultLocation); // Set pin to default location
    setMapLocation(DefaultLocation);
    setZoom(DefaultZoom);
    setMapKey(prev => prev + 1); // Force map to re-render with default location
    setModalOpen(true);
  };
  const handleCloseModal = () => {
    setModalOpen(false);
    // Reset all fields when closing modal
    setAddress('');
    setAddressTitle('');
    setSearchQuery('');
    setSelectedLocation(null);
  };

  const [defaultLocation, setDefaultLocation] = useState(DefaultLocation);

  const [addressTitle, setAddressTitle] = useState('');
  const [address, setAddress] = useState('');
  const [locations, setLocations] = useState([])
  const [selectedLocation, setSelectedLocation] = useState(null);

  const [companyId, setCompanyId] = useState(0)

  const [mapLocation, setMapLocation] = useState(DefaultLocation);
  const [zoom, setZoom] = useState(DefaultZoom);
  const [isOnlineOnly, setIsOnlineOnly] = useState(false);
  const [countries, setCountries] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [isGlobal, setIsGlobal] = useState(false);
  const [isAddingLocation, setIsAddingLocation] = useState(false);
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const [hasUserLocation, setHasUserLocation] = useState(false);
  const [mapKey, setMapKey] = useState(0); // Key to force map re-render when needed
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const reverseGeocodeRef = useRef(null);

  const handleAddressResolved = (resolvedAddress) => {
    setAddress(resolvedAddress);
  };

  const handleSelectLocation = (location, nextZoom = zoom, options = {}) => {
    setSelectedLocation(location);
    setHasUserLocation(false);
    if (options.markerOnly) return;
    setMapLocation(location);
    if (nextZoom != null) setZoom(nextZoom);
  };

  // Fetch countries and read URL params once on mount
  useEffect(() => {
    const search = window.location.search;
    const query = new URLSearchParams(search);
    setCompanyId(+query.get('companyId'));
    getCountries();
  }, []);

  // Keep local state in sync with context on initial mount (same pattern as old Step7)
  useEffect(() => {
    if (wizardData.locations) {
      setLocations(wizardData.locations.list || []);
      setIsOnlineOnly(wizardData.locations.isOnlineOnly || false);
      setSelectedCountries(wizardData.locations.countries || []);
      setIsGlobal(wizardData.locations.isGlobal || false);
    }
  }, []);

  // Pop-In deals are offline-only: force online-only off and clear country/global state
  useEffect(() => {
    if (!isPopin) return;

    const shouldReset =
      !!locationsData.isOnlineOnly ||
      (locationsData.countries && locationsData.countries.length > 0) ||
      !!locationsData.isGlobal;

    if (shouldReset) {
      updateData('locations', { isOnlineOnly: false, countries: [], isGlobal: false });
    }

    if (isOnlineOnly) setIsOnlineOnly(false);
    if (selectedCountries.length) setSelectedCountries([]);
    if (isGlobal) setIsGlobal(false);
  }, [isPopin]);

  useEffect(() => {
    if (countries && countries.length > 0 && wizardData.meta.userCountry) {
      console.log('Countries loaded, attempting to center map');
      centerMapOnUserCountry();
    }
  }, [countries, wizardData.meta.userCountry]);

  // Validation logic: require either locations or online-only setting
  useEffect(() => {
    const hasLocations = locationsData.list && locationsData.list.length > 0;
    const isOnlineOnlyValid = locationsData.isOnlineOnly && (
      locationsData.isGlobal || 
      (locationsData.countries && locationsData.countries.length > 0)
    );
    
    // Enable continue button only if user has either:
    // 1. Added at least one location (when not online-only), OR
    // 2. Set as online-only with valid country selection
    const isValid = isPopin ? hasLocations : (hasLocations || isOnlineOnlyValid);
    
    setDisableBtn(!isValid);
  }, [locationsData.list, locationsData.isOnlineOnly, locationsData.isGlobal, locationsData.countries, isPopin, setDisableBtn]);

  const getCountries = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URI}/v3/companies/countries`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      })
      const data = await res.json()

      if (data.status == 'success') {
        // Add "Available Worldwide" option to the countries array
        const countriesWithGlobal = [
          { id: "global", name: "Available Worldwide" },
          ...data.data
        ];
        setCountries(countriesWithGlobal)
        console.log('Countries:', countriesWithGlobal)
      }
    } catch (error) {
      console.log('Error fetching countries:', error)
    }
  }

  function handleChangeZoom(newZoom) {
    setZoom(newZoom);
  }

  function handleResetLocation() {
    setDefaultLocation({ ...DefaultLocation });
    setZoom(DefaultZoom);
  }
  const getUserLocation = (reverseGeocode) => {
    setIsDetectingLocation(true);

    if (!navigator.geolocation) {
      setIsDetectingLocation(false);
      centerMapOnUserCountry();
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const userLat = position.coords.latitude;
        const userLng = position.coords.longitude;
        const newLocation = { lat: userLat, lng: userLng };

        handleSelectLocation(newLocation, 15);
        setHasUserLocation(true);
        setMapKey((prev) => prev + 1);
        reverseGeocode?.(userLat, userLng);
        setIsDetectingLocation(false);
      },
      (error) => {
        console.log('Error getting user location:', error);
        setIsDetectingLocation(false);
        centerMapOnUserCountry();
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000,
      }
    );
  };

  // Function to center map on user's country based on IP
  const centerMapOnUserCountry = () => {
    const userCountryCode = wizardData.meta.userCountry;
    
    // Safety check: ensure we have valid data
    if (!userCountryCode || !countries || countries.length === 0) {
      console.log('Missing required data for centering map');
      return;
    }
    
    const countryData = countries.find(country => 
      country.iso?.toLowerCase() === userCountryCode?.toLowerCase() || 
      country.iso === userCountryCode
    );
    
       if (countryData) {
        const lat = parseFloat(countryData.latitude);
        const lng = parseFloat(countryData.longitude);
        
        if (!isNaN(lat) && !isNaN(lng)) {
          const newLocation = { lat: lat, lng: lng };
          setMapLocation(newLocation);
          setDefaultLocation(newLocation);
          setSelectedLocation(newLocation);
          console.log('Centered map on user country:', countryData.name, newLocation);
        } else {
          // Fallback to default location if coordinates are invalid
          setMapLocation(DefaultLocation);
          setDefaultLocation(DefaultLocation);
          setSelectedLocation(DefaultLocation);
          console.log('Invalid coordinates, using default location');
        }
     } else {
      // Default fallback
      setMapLocation(DefaultLocation);
      setDefaultLocation(DefaultLocation);
      setSelectedLocation(DefaultLocation);
      console.log('Using default location');
    }
  };

  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: "80%",
    height: "80%",
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 2,
    borderRadius: '5px',
  };

  const handleAddressInputChange = (value) => {
    setAddress(value);
  };

  const addAddressTitle = (value) => {
    setAddressTitle(value);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const addAddressToArray = async () => {
    setIsAddingLocation(true);
    
    const locationToUse = selectedLocation || mapLocation;
    
    const newLocation = {
      title: addressTitle,
      address: address,
      latitude: locationToUse.lat,
      longitude: locationToUse.lng
    };
    
    const currentLocations = locationsData.list || [];
    updateData('locations', {
      list: [...currentLocations, newLocation]
    });
    
    setAddress('');
    setAddressTitle('');
    setSearchQuery('');
    setSelectedLocation(null);
    handleCloseModal();
    setIsAddingLocation(false);
  }

  const deleteLocation = (index) => {
    const tempArray = (locationsData.list || []).slice()
    tempArray.splice(index, 1);
    updateData('locations', { 
      list: tempArray 
    })
  }

  const handleOnlineOnlyToggle = (event) => {
    if (isPopin) return;
    setIsOnlineOnly(event.target.checked);
    if (event.target.checked) {
      // Clear locations when switching to online only
      updateData('locations', { 
        list: [] 
      });
    }
  };

  const handleCountryChange = (event, newValue) => {
    console.log('Country change:', newValue);
    
    if (Array.isArray(newValue)) {
      // Check if "Available Worldwide" is selected
      const hasGlobalOption = newValue.some(item => item && item.id === "global");
      
      if (hasGlobalOption) {
        setIsGlobal(true);
        setSelectedCountries([]);
      } else {
        setIsGlobal(false);
        setSelectedCountries(newValue.filter(item => {
          return item && countries.some(country => country.id === item.id);
        }));
      }
    } else {
      setIsGlobal(false);
      setSelectedCountries([]);
    }
  };

  // Update DataContext when online-only or countries change
  const isInitialMount = useRef(true);
  
  useEffect(() => {
    // Skip the initial mount to prevent overwriting existing data
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    
    if (isOnlineOnly) {
      updateData('locations', {
        list: [],
        isOnlineOnly: true,
        countries: isGlobal ? [] : selectedCountries,
        isGlobal: isGlobal
      });
    } else {
      updateData('locations', {
        list: locations, // Use local state instead of context
        isOnlineOnly: false,
        countries: [],
        isGlobal: false
      });
    }
  }, [isOnlineOnly, selectedCountries, isGlobal]);

  const getLocationTableItem = (arr) => {
    if (arr?.length == 0) return
    return arr.map((item, index) => (
      <div key={index} className="location-item">
        <div className="address-title">{item.title}</div>
        <div className="address-address">{item.address}</div>
        <div className="coordinates">
          {item.latitude && item.longitude 
            ? `${item.latitude.toFixed(6)}, ${item.longitude.toFixed(6)}`
            : 'N/A'
          }
        </div>
        <CloseRoundedIcon className="close" onClick={() => deleteLocation(index)} />
      </div>
    ))
  }

  return (
    <>
      <div className="container">
        <div className="step7-container">
          <div className="step7" style={{paddingBottom: '50px'}}>
            <div className="step7-heading">
              <h1>Specify Locality</h1>
              <h3>
                {isPopin
                  ? 'Add the off-line locations that will honor your promotional code'
                  : 'Add the online or off-line locations that will honor your promotional code'}
              </h3>
            </div>
            
            {/* Online Only Toggle */}
            {!isPopin && (
              <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isOnlineOnly}
                      onChange={handleOnlineOnlyToggle}
                      color="primary"
                    />
                  }
                  label="This coupon is only available online"
                />
              </div>
            )}

            {/* Countries Dropdown - Show only when online only is enabled */}
            {!isPopin && isOnlineOnly && (
              <div style={{ marginBottom: '20px' }}>
                <Autocomplete
                  multiple={!isGlobal}
                  value={ isGlobal ? countries.find(c => c.id === "global") || null : selectedCountries }
                  onChange={handleCountryChange}
                  filterOptions={(options, params) => {
                    const { inputValue } = params;
                    return options.filter(
                      (option) =>
                        option.name.toLowerCase().includes(inputValue.toLowerCase())
                    );
                  }}
                  selectOnFocus
                  clearOnBlur
                  handleHomeEndKeys
                  id="country-list"
                  options={countries}
                  getOptionLabel={(option) => option.name || ""}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderOption={(props, option) => <li {...props}>{option.name}</li>}
                  freeSolo={false}
                  className="countries-autocomplete"
                  limitTags={3}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.id}
                        label={option.name}
                        size="small"
                        style={{
                          maxWidth: '120px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      label={!isGlobal && selectedCountries.length === 0 ? "Select Applicable Countries" : ""}
                      placeholder={!isGlobal && selectedCountries.length === 0 ? "Select Applicable Countries" : ""}
                    />
                  )}
                />
                <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                  {isGlobal 
                    ? "This coupon will be available online globally" 
                    : "Select countries where this coupon will be available online"
                  }
                </p>
              </div>
            )}

            {/* Physical Locations Section - Show only when online only is disabled */}
            {!isOnlineOnly && (
              <>
                {/* Locations Table - Only show when there are locations */}
                {locationsData.list && locationsData.list.length > 0 && (
                  <div className="locations-table">
                    <div className="table-head">
                      <div className="title">Title</div>
                      <div className="address">Address</div>
                      <div className="coordinates">Coordinates</div>
                    </div>
                    <div className="table-body">
                      {getLocationTableItem(locationsData.list)}
                    </div>
                  </div>
                )}

                {/* Add New Location Button */}
                <div className="create-new" onClick={handleOpenModal}>
                  <AddRoundedIcon /> <span>ADD NEW LOCATION</span>
                </div>

                {/* Helper text - only show when no locations */}
                {(!locationsData.list || locationsData.list.length === 0) && (
                  <p style={{ 
                    textAlign: 'center', 
                    color: '#6f7f9f', 
                    fontSize: '14px', 
                    margin: '10px 0 0 0',
                    fontFamily: 'Mulish',
                    fontStyle: 'italic'
                  }}>
                    You can add multiple locations to expand your coupon's reach
                  </p>
                )}
              </>
            )}

            {/* Validation Message - At the bottom */}
            {(() => {
              const hasLocations = locationsData.list && locationsData.list.length > 0;
              const isOnlineOnlyValid = locationsData.isOnlineOnly && (
                locationsData.isGlobal || 
                (locationsData.countries && locationsData.countries.length > 0)
              );
              const isValid = isPopin ? hasLocations : (hasLocations || isOnlineOnlyValid);
              
              if (!isValid) {
                return (
                  <div style={{ 
                    marginTop: '20px', 
                    padding: '15px', 
                    backgroundColor: '#fff3cd', 
                    border: '1px solid #ffeaa7',
                    borderRadius: '8px',
                    color: '#856404'
                  }}>
                    <strong>Action Required:</strong>{' '}
                    {isPopin
                      ? 'You must add at least one physical location to continue.'
                      : 'You must either add at least one physical location or enable \"online only\" with country selection to continue.'}
                  </div>
                );
              }
              return null;
            })()}
          </div>
        </div>
        <Modal
          open={modalOpen}
          onClose={handleCloseModal}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={style}>
            <div className="new-location-container">
              <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
                <LocationMapPicker
                  mapKey={mapKey}
                  mapLocation={mapLocation}
                  zoom={zoom}
                  selectedLocation={selectedLocation}
                  searchQuery={searchQuery}
                  onSearchChange={handleSearchChange}
                  isSearching={isSearching}
                  setIsSearching={setIsSearching}
                  onSelectLocation={handleSelectLocation}
                  onAddressResolved={handleAddressResolved}
                  hasUserLocation={hasUserLocation}
                  isDetectingLocation={isDetectingLocation}
                  onDetectLocation={getUserLocation}
                  onClose={handleCloseModal}
                  reverseGeocodeRef={reverseGeocodeRef}
                />
              </APIProvider>
              <div className="address-title-input">
                <div className="input input-address">
                  <label>ADDRESS</label>
                  <input
                    value={address}
                    type="text"
                    placeholder="Enter Your Address"
                    onChange={(e) => handleAddressInputChange(e.target.value)}
                  />
                </div>
                <div className="input">
                  <label>TITLE</label>
                  <input value={addressTitle} type="text" placeholder='Assign a title for your address' onChange={(e) => addAddressTitle(e.target.value)} />
                </div>
                <Button 
                  className="blue-btn" 
                  onClick={() => addAddressToArray()} 
                  disabled={isAddingLocation || !addressTitle.trim() || !address.trim() || !selectedLocation}
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    gap: '8px',
                    minWidth: '100px'
                  }}
                >
                  {isAddingLocation ? (
                    <>
                      <CircularProgress size={16} style={{ color: 'white' }} />
                    </>
                  ) : (
                    'Add'
                  )}
                </Button>
              </div>
            </div>
          </Box>
        </Modal>

      </div >

    </>

  )
}

export default Locations
